import { ParseResult } from "papaparse";
import React, { useState, useEffect, useContext } from "react";
import IOnCreateOrderEntryItemsDTO from "../../../models/DTO/IOnCreateOrderEntryItemsDTO";
import ICatalog_Item from "../../../models/ICatalog_Item";
import INotification from "../../../models/INotification";
import IOrder from "../../../models/IOrder";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OwCsvReader from "../../shared/OwCsvReader/OwCsvReader";
import OwTitle from "../../shared/OwTitle/OwTitle";
import WhatsThis from "../../shared/WhatsThis/WhatsThis";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "@contexts/SocketContext";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import VendorContext from "@contexts/VendorContext";
import { IAddBulkItems } from "@models/IAddBulkItems";
import { isNumber } from "lodash-es";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import { CustomImportUploader } from "@components/shared/CustomImportUploader/CustomImportUploader";
import CustomImportTypeEnum from "@models/CustomImportTypeEnum";

type Props = {
    orderGuid?: string;
    orderModel?: IOrder;
    isReadOnly: boolean;
    addBulkItems: (items: IAddBulkItems[]) => void;
    getOrder: () => void;
};

const OrderEntryCsvImport: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const getInitialImportType = () => {
        if (supplierModel.has_csv_order_entry && supplierModel.has_custom_imports) {
            return "custom";
        } else if (supplierModel.has_csv_order_entry) {
            return "csv";
        } else if (supplierModel.has_custom_imports) {
            return "custom";
        }

        return "csv";
    };

    const hasBothImportTypes = supplierModel.has_csv_order_entry && supplierModel.has_custom_imports;
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedImportType, setSelectedImportType] = useState<"csv" | "custom">(getInitialImportType());

    const onCreateOrderEntryItems = (data: IOnCreateOrderEntryItemsDTO) => {
        if (isProcessing) {
            const notification: INotification = {
                message: "successfully_imported_csv",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.br,
            };

            uiContext.notifyFn(null, notification);
        }

        setIsProcessing(false);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.OrderEntryOnCreateOrderEntryItems, fn: onCreateOrderEntryItems }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const csvLoaded = (csvData: ParseResult<any>, filename: string) => {
        // console.log("csvLoaded: ", csvData);
        let qtyCol = "";
        let itemGuidCol = "";
        let skuCol = "";

        const firstRow: object = csvData.data[0];

        for (const col of Object.keys(firstRow)) {
            if (col.toLowerCase().indexOf("qty") >= 0) {
                qtyCol = col;
            }

            if (col.toLowerCase().indexOf("guid") >= 0) {
                itemGuidCol = col;
            }

            if (col.toLowerCase().indexOf("sku") >= 0) {
                skuCol = col;
            }
        }

        // for each row, get the
        const items: IAddBulkItems[] = [];

        for (const row of csvData.data) {
            const qty = Number(row[qtyCol]);

            if (isNumber(qty) && qty) {
                items.push({
                    item: {
                        qty,
                        Catalog_Item: {
                            guid: row[itemGuidCol],
                            sku: row[skuCol],
                        } as ICatalog_Item,
                        catalog_item_id: 0, // we don't have this....
                        catalog_item_guid: row[itemGuidCol],
                        item_no: 0,
                    },
                });
            }
        }

        setIsProcessing(true);
        props.addBulkItems(items);
    };

    const handleImportTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedImportType(e.target.value as "csv" | "custom");
    };

    const showCsvImport = supplierModel.has_csv_order_entry && (!hasBothImportTypes || selectedImportType === "csv");
    const showCustomImport = supplierModel.has_custom_imports && (!hasBothImportTypes || selectedImportType === "custom");

    return (
        <div className="OrderEntryCsvImport">
            <WhatsThis subject={"csv_import_detail"} />

            {hasBothImportTypes && (
                <div className="mb-3">
                    <div className="d-inline-flex align-items-center me-4">
                        <label className="pointer" htmlFor="csv">
                            <OwFnInput
                                type="radio"
                                id="csv"
                                name="importType"
                                value="csv"
                                checked={selectedImportType === "csv"}
                                fn={handleImportTypeChange}
                                disabled={props.isReadOnly}
                                noOuterDiv={true}
                            />
                            <span className="ms-2">
                                <OwTranslate id="csv" defaultMessage="CSV Import" />
                            </span>
                        </label>
                    </div>
                    <div className="d-inline-flex align-items-center">
                        <label className="pointer" htmlFor="custom">
                            <OwFnInput
                                id="custom"
                                type="radio"
                                name="importType"
                                value="custom"
                                checked={selectedImportType === "custom"}
                                fn={handleImportTypeChange}
                                disabled={props.isReadOnly}
                                noOuterDiv={true}
                            />
                            <span className="ms-2">
                                <OwTranslate id="custom_import" defaultMessage="Custom Import" />
                            </span>
                        </label>
                    </div>
                </div>
            )}

            {showCsvImport && <OwCsvReader onFileLoaded={csvLoaded} isProcessing={isProcessing} />}

            {showCustomImport && (
                <div className="custom-import-section">
                    <CustomImportUploader
                        type={CustomImportTypeEnum.ORDER}
                        existingOrderGuid={props.orderGuid}
                        onProcessed={props.getOrder}
                    />
                </div>
            )}
        </div>
    );
};

export default OrderEntryCsvImport;
